import React from "react";
import { Box, Button, Link } from "@mui/material";

import HeroSection from "../components/HeroSection";
import ContentSection from "../components/ContentSection";

import RoadUnderBlueSky from "../assets/images/RoadUnderBlueSky.jpg";
import TrucksOnASunnyRoad from "../assets/images/TrucksOnASunnyRoad.jpg";
import BlueDomesBioLngFacility from "../assets/images/BlueDomesBioLngFacility.jpg";
import FieldsUnderSky from "../assets/images/FieldsUnderSky.jpg";
import BioLngFacilitiesIndustrial from "../assets/images/BioLngFacilitiesIndustrial.png";
import RoadAerialView from "../assets/images/RoadAerialView.jpeg";

import {
  Public,
  School,
  TrendingUp,
  Assessment,
  LocalShipping,
  Handshake,
  VerifiedUser,
  Store,
  AutoGraph,
  Recycling,
  Agriculture,
  LooksOne,
  Looks3,
  LooksTwo,
  Looks4,
  Looks5,
  AccountBalanceWallet,
  Forest,
  AccountBalanceWalletOutlined,
} from "@mui/icons-material";
import StyledCard from "../components/StyledCard";
import Badge from "../components/ui/Badge";
import Label from "../components/ui/Label";
import SectionDescription from "../components/SectionDescription";
import Heading from "../components/ui/Heading";
import HalvedImageSection from "../components/HalvedImageSection";
import Highlight from "../components/ui/Highlight";
import HalvedContentSection from "../components/HalvedContentSection";
import GridLayout from "../components/GridLayout";
import UltraWideBgSection from "../components/UltraWideBgSection";
import ImageCard from "../components/ImageCard";
import { CompanyInfo } from "./constants";

const ReasonsToChooseGyrfalcon = Object.freeze([
  {
    title: "Zero Upfront Cost",
    description:
      "No need for expensive infrastructure investments—continue operations efficiently with little financial burden.",
    icon: AccountBalanceWallet,
  },
  {
    title: "Stable, Predictable Revenue",
    description:
      "Long-term contracts provide consistent monthly payments, ensuring financial security.",
    icon: TrendingUp,
  },
  {
    title: "Expertise Where It Matters",
    description:
      "Gyrfalcon brings specialised knowledge in biogas purification and BioLNG production, so producers can stay focused on their core business while benefiting from a reliable revenue stream.",
    icon: School,
  },
  {
    title: "Greater Market Reach",
    description:
      "BioLNG can be stored and transported, providing access to larger and more profitable markets than traditional biogas.",
    icon: Public,
  },
]);

const Solutions = Object.freeze([
  {
    title: "Assessment & Agreement",
    description:
      "We assess your biogas production potential and offer competitive purchasing terms.",
    icon: Assessment,
  },
  {
    title: "Biogas Collection & Processing",
    description:
      "We handle transportation, purification, and liquefaction at our high-tech facilities.",
    icon: LocalShipping,
  },
  {
    title: "Long-Term Partnership",
    description:
      "Secure your business with reliable off-take agreements and stable income.",
    icon: Handshake,
  },
]);

const WhatYouGain = Object.freeze([
  {
    title: "Consistent Revenue",
    description: "Secure long-term payments for your biogas.",
    icon: AccountBalanceWalletOutlined,
  },
  {
    title: "Access to Larger Markets",
    description:
      "BioLNG is in high demand in logistics, shipping, and industry.",
    icon: Public,
  },
  {
    title: "Sustainability Compliance",
    description: "Meet emission reduction targets with a profitable solution.",
    icon: VerifiedUser,
  },
  {
    title: "Hassle-Free Operations",
    description:
      "We take care of everything—collection, processing, and distribution.",
    icon: Store,
  },
]);

const Advantages = Object.freeze([
  {
    title: "Guaranteed Offtake Agreements",
    description: "We purchase biogas at competitive, fixed rates.",
    icon: Handshake,
  },
  {
    title: "Future-Proof Business Model",
    description:
      "Shift from unpredictable electricity tariffs to a stable revenue stream.",
    icon: AutoGraph,
  },
  {
    title: "Seamless Process",
    description:
      "No need to invest in expensive purification or liquefaction equipment—we handle it all.",
    icon: LocalShipping,
  },
  {
    title: "Certified Sustainability",
    description:
      "Our BioLNG meets European industry and environmental standards.",
    icon: VerifiedUser,
  },
]);

const Process = Object.freeze([
  {
    title: "Get in Touch",
    description:
      "Let’s discuss your biogas production and potential partnership.",
    icon: LooksOne,
  },
  {
    title: "Assessment & Proposal",
    description:
      "We offer a competitive purchase agreement tailored to your needs.",
    icon: LooksTwo,
  },
  {
    title: "Biogas Collection & Processing",
    description:
      "We collect and convert your biogas into BioLNG at our state-of-the-art facilities.",
    icon: Looks3,
  },
  {
    title: "Reliable Payments",
    description:
      "You receive regular payments while we manage the entire conversion process.",
    icon: Looks4,
  },
  {
    title: "Ongoing Support",
    description:
      "Our partnership ensures long-term profitability and market growth.",
    icon: Looks5,
  },
]);

const EnvironmentalImpact = Object.freeze([
  {
    title: "Waste-to-Value Model",
    description:
      "Maximise your biogas potential instead of flaring or underutilising it.",
    icon: Recycling,
  },
  {
    title: "Supporting Clean Energy",
    description:
      "BioLNG plays a key role in decarbonising transport and industry.",
    icon: Forest,
  },
  {
    title: "Strengthening Agriculture & Industry",
    description:
      "Supporting farmers, waste processors, and businesses to drive sustainability and profitability.",
    icon: Agriculture,
  },
]);

export default function HomePage() {
  return (
    <>
      <HeroSection
        backgroundImage={RoadUnderBlueSky}
        heading="Maximise the Value of Your Biogas with Gyrfalcon"
      >
        <SectionDescription color="white" maxWidth="900px" margin="0 auto">
          We provide a seamless pathway for farmers, agribusinesses, and waste
          processors in Europe to turn their biogas into profit. By purchasing
          raw biogas and converting it into Bio-LNG, we help producers move
          beyond electricity Feed-in Tariffs and unlock new revenue streams in
          the renewable energy market.
        </SectionDescription>
        <Button
          color="tertiary"
          disableElevation
          // endIcon={<Send />}
          onClick={() => {
            window.location.href = `mailto:${CompanyInfo.email}`;
          }}
          size="large"
          sx={{
            marginTop: 6,
          }}
          variant="contained"
        >
          Partner with Us
        </Button>
      </HeroSection>
      <ContentSection>
        <HalvedImageSection
          image={BioLngFacilitiesIndustrial}
          imagePosition="right"
          content={
            <>
              <Box alignItems="start" display="flex" gap={1}>
                {["Sustainability", "Innovation", "Profitability"].map(
                  (item) => (
                    <Badge outlined key={item}>
                      {item}
                    </Badge>
                  )
                )}
              </Box>
              <Heading level="h2" marginTop={2}>
                About Us
              </Heading>
              <SectionDescription marginTop={2}>
                Gyrfalcon is at the forefront of Bio-LNG production, working
                with equipment manufacturers, biogas suppliers, agricultural
                enterprises, and waste processors to transform their raw biogas
                into a high-value energy source.
              </SectionDescription>
            </>
          }
        />
        <HalvedImageSection
          marginTop={{ xs: 4, lg: undefined }}
          image={RoadAerialView}
          imagePosition="left"
          content={
            <>
              <Heading level="h2">Our Mission</Heading>
              <SectionDescription marginTop={2}>
                To create a sustainable, profitable, and low-carbon future by
                offering biogas producers long-term purchasing agreements and
                ensuring efficient utilisation of their energy potential.
              </SectionDescription>
            </>
          }
          reverseMobile
        />
      </ContentSection>
      <UltraWideBgSection marginTop={4}>
        <ContentSection
          textAlign={{ xs: "left", lg: "center" }}
          marginTop={{ xs: undefined, lg: 4 }}
        >
          <Label>Why It Matters</Label>
          <Heading level="h2" margin="12px auto 0" maxWidth="800px">
            Unlocking New Opportunities for Biogas Producers
          </Heading>
          <SectionDescription maxWidth="1000px" margin="0 auto" marginTop={2}>
            Once Feed-in Tariffs expire, biogas producers face limited options
            to maintain profitability. Investing in new technology to process
            biogas independently requires significant capital and expertise,
            posing financial and operational risks. Additionally, biomethane
            injection into the gas grid is often unfeasible due to distance
            constraints and additional infrastructure costs. Partnering with
            Gyrfalcon allows biogas producers to{" "}
            <Highlight>focus on what they do best</Highlight> — agriculture and
            waste management —{" "}
            <Highlight>
              while we handle the complexities of BioLNG production.
            </Highlight>{" "}
            In some cases,{" "}
            <Highlight>we can also partner with biogas plants</Highlight> that
            are interested in investing in BioLNG technologies, creating a
            collaborative approach to maximise efficiency and profitability.
          </SectionDescription>
        </ContentSection>
      </UltraWideBgSection>
      <UltraWideBgSection backgroundColor="white">
        <ContentSection>
          <HalvedContentSection
            leftContent={
              <>
                <Label>Future-Proof Your Biogas Business</Label>
                <Heading level="h4" marginTop={2}>
                  Why Choose BioLNG Over Electricity Generation?
                </Heading>
                <SectionDescription marginTop={2}>
                  With increasing support for BioLNG, now is the time to
                  transition. By partnering with Gyrfalcon, biogas producers can
                  stay ahead of policy changes while ensuring long-term
                  profitability.
                </SectionDescription>
              </>
            }
            rightContent={
              <GridLayout
                items={ReasonsToChooseGyrfalcon.map((item) => {
                  return <StyledCard {...item} key={item.title} />;
                })}
                itemsPerRow={2}
              />
            }
          />
        </ContentSection>
      </UltraWideBgSection>
      <UltraWideBgSection>
        <ContentSection>
          <HalvedContentSection
            rightContent={
              <>
                <Label>How It Works</Label>
                <Heading level="h3" marginTop={2}>
                  A Simple and Profitable Partnership
                </Heading>
                <SectionDescription marginTop={2}>
                  We provide an easy, hassle-free solution for biogas producers
                  to monetise their production.
                </SectionDescription>
              </>
            }
            leftContent={
              <GridLayout
                items={[
                  <ImageCard key="image" image={BlueDomesBioLngFacility} />,
                  ...Solutions.map((item) => {
                    return <StyledCard {...item} key={item.title} />;
                  }),
                ]}
                itemsPerRow={2}
              />
            }
            reverseMobile
          />
        </ContentSection>
      </UltraWideBgSection>
      <UltraWideBgSection backgroundColor="white">
        <ContentSection>
          <HalvedContentSection
            leftContent={
              <>
                <Heading level="h3">What You Gain</Heading>
              </>
            }
            rightContent={
              <GridLayout
                items={WhatYouGain.map((item) => {
                  return <StyledCard {...item} key={item.title} />;
                })}
                itemsPerRow={2}
              />
            }
          />
        </ContentSection>
      </UltraWideBgSection>
      <UltraWideBgSection>
        <ContentSection>
          <HalvedContentSection
            rightContent={
              <>
                <Label>The Gyrfalcon Advantage</Label>
                <Heading level="h3" marginTop={2}>
                  Why Partner with Gyrfalcon?
                </Heading>
              </>
            }
            leftContent={
              <GridLayout
                items={Advantages.map((item) => {
                  return <StyledCard {...item} key={item.title} />;
                })}
                itemsPerRow={2}
              />
            }
            reverseMobile
          />
        </ContentSection>
      </UltraWideBgSection>

      <ContentSection marginTop={6} marginBottom={6}>
        <Box textAlign={{ xs: "left", lg: "center" }}>
          <Label>How We Work with You</Label>
          <Heading level="h3" marginTop={2}>
            Our Process
          </Heading>
        </Box>

        <Box
          marginTop={4}
          display="flex"
          gap={2}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {Process.map((item) => {
            return (
              <Box key={item.title} flex="1">
                <StyledCard {...item} dark />{" "}
              </Box>
            );
          })}
        </Box>
      </ContentSection>
      <UltraWideBgSection>
        <ContentSection>
          <HalvedContentSection
            leftContent={
              <>
                <Label>Environmental Impact</Label>
                <Heading level="h3" marginTop={2}>
                  Building a Cleaner, More Profitable Future
                </Heading>
                <SectionDescription marginTop={2}>
                  By partnering with Gyrfalcon, biogas producers turn waste into
                  revenue while reducing emissions.
                </SectionDescription>
              </>
            }
            rightContent={
              <GridLayout
                items={[
                  ...EnvironmentalImpact.map((item) => {
                    return <StyledCard {...item} key={item.title} />;
                  }),
                  <ImageCard key="image" image={FieldsUnderSky} />,
                ]}
                itemsPerRow={2}
              />
            }
          />
        </ContentSection>
      </UltraWideBgSection>

      <ContentSection>
        <HalvedImageSection
          image={TrucksOnASunnyRoad}
          imagePosition="right"
          content={
            <>
              <Label>Contact Us</Label>
              <Heading level="h3" marginTop={2}>
                Join the Future of Sustainable Energy
              </Heading>
              <SectionDescription marginTop={2}>
                <Link
                  href={`mailto:${CompanyInfo.email}`}
                  sx={{ color: "inherit" }}
                >
                  Contact us
                </Link>{" "}
                today and discover how partnering with Gyrfalcon can transform
                your biogas into BioLNG profitably and sustainably.
              </SectionDescription>
            </>
          }
        />
      </ContentSection>
    </>
  );
}
